import { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { BsPersonPlus, BsCheckCircle } from 'react-icons/bs';

import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import { getInitials } from '#lib/textUtils';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import { useUpdateDeal } from '../../../models/deals/mutations';

const OwnerDropdown = ({ deal }) => {
	const [open, setOpen] = useState(false);
	const updateDeal = useUpdateDeal();
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];

	const dealUserIds = deal.users?.map((u) => u.id) ?? [];
	const firstUser = deal.users?.[0];
	const ownerLabel = firstUser?.fullName ?? firstUser?.emailAddress ?? null;

	const closeMenu = useCallback(() => setOpen(false), []);

	const handleToggleAssign = (memberId) => {
		const next = dealUserIds.includes(memberId) ? null : memberId;
		setOpen(false);
		updateDeal.mutate({ id: deal.id, payload: { userId: next } });
	};

	const handleUnassignAll = () => {
		setOpen(false);
		updateDeal.mutate({ id: deal.id, payload: { userId: null } });
	};

	const trigger = (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				setOpen((p) => !p);
			}}
			className={`h-7 w-7 rounded-full flex items-center justify-center text-xs text-white transition-shadow hover:ring-2 hover:ring-primary-300 ${
				dealUserIds.length > 0 ? 'bg-primary-500' : 'bg-bgBase-600'
			}`}
			title={ownerLabel ?? 'Nicht zugewiesen — klicken zum Zuweisen'}
		>
			{dealUserIds.length > 0 ? getInitials(ownerLabel) : <BsPersonPlus className="w-4 h-4" />}
		</button>
	);

	return (
		<Popover open={open} onClose={closeMenu} trigger={trigger} size="s" align="end">
			<Panel.Body padded={false} className="max-h-48" onClick={(e) => e.stopPropagation()}>
				<Menu>
					<Menu.Label>Deal-Besitzer</Menu.Label>
					{members.map((m) => (
						<Menu.Item
							key={m.userId}
							onClick={() => handleToggleAssign(m.userId)}
							selected={dealUserIds.includes(m.userId)}
							trailing={
								dealUserIds.includes(m.userId) ? (
									<BsCheckCircle className="w-3 h-3 text-primary-500" />
								) : undefined
							}
						>
							<span className="flex items-center gap-2">
								<span className="h-5 w-5 rounded-full flex items-center justify-center text-[8px] font-semibold text-white flex-shrink-0 bg-primary-500">
									{getInitials(m.fullName ?? m.email)}
								</span>
								<span className="truncate">{m.fullName ?? m.email}</span>
							</span>
						</Menu.Item>
					))}
					{dealUserIds.length > 0 && (
						<>
							<Menu.Separator />
							<Menu.Item onClick={handleUnassignAll}>Zuweisung entfernen</Menu.Item>
						</>
					)}
				</Menu>
			</Panel.Body>
		</Popover>
	);
};

OwnerDropdown.propTypes = {
	deal: PropTypes.object.isRequired,
};

export default OwnerDropdown;
